<template>
  <FormField
    :id="id"
    :class="wrapperClass"
    :label="label"
    :label-pin="labelPin"
    :is-disabled="isDisabled"
    :is-required="isRequired"
    :is-label-visible="isLabelVisible"
    :is-field-focused="isFocused || modelValue.length > 0"
    :is-error="isError"
    :message-fixed="messageFixed"
    :message="message"
    :is-message-visible="isMessageVisible"
  >
    <div
      :class="{
        'form-textarea-grow': autoGrow
      }"
    >
      <textarea
        :id="id"
        ref="inputRef"
        v-bind="$attrs"
        :value="modelValue"
        :disabled="isDisabled"
        :placeholder="placeholder"
        :class="[inputClass]"
        @input="updateValue"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
      <FormBorder :is-error="isError" />
    </div>
    <slot />
  </FormField>
</template>

<script setup lang="ts">
import type { FormFieldProps } from './FormField.vue'

type FormTextareaProps = {
  inputClass?: string
  modelValue: string | number | null
  placeholder?: string
  autoGrow?: boolean
} & Pick<
  FormFieldProps,
  | 'wrapperClass'
  | 'label'
  | 'labelClass'
  | 'labelPin'
  | 'isLabelVisible'
  | 'isRequired'
  | 'isDisabled'
  | 'isError'
  | 'messageFixed'
  | 'message'
  | 'isMessageVisible'
>

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(defineProps<FormTextareaProps>(), {
  autoGrow: true,
  inputClass: `form-textarea`,
  modelValue: '',
  placeholder: '',
  isLabelVisible: true,
  labelPin: true
})

const emit = defineEmits(['update:modelValue'])

const updateValue = (e: Event) => {
  emit('update:modelValue', (e.target as HTMLInputElement).value)
  if (!props.autoGrow && !inputRef.value) return
  inputRef.value.parentNode.dataset.replicatedValue = (e.target as HTMLInputElement).value
}

const inputRef = ref<null | HTMLElement>(null)
const id = useId()
const isFocused = ref(false)
</script>

<style lang="postcss">
/* https://codepen.io/chriscoyier/pen/XWKEVLy */
.form-textarea-grow {
  display: grid;

  &::after {
    content: attr(data-replicated-value) ' '; /* Problem with TailwindCSS */
    visibility: hidden;
    white-space: pre-wrap;
    grid-area: 1 / 1 / 2 / 2;
  }

  > textarea {
    resize: none;
    overflow: hidden;
    grid-area: 1 / 1 / 2 / 2;
  }
}
</style>
